import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai';
import { Button } from '@mui/material';
import { CenteredFlexBox, ColumnFlexBox } from '../../../Components/FlexBox';
import SchemaWrapper, {
  SINGLE_SCHEMA,
} from '../../../Components/engine/SchemaWrapper';
import { SchemaParametersProvider } from '../../../Components/engine/SchemaParameters';
import { genericFormAtom } from '../../../store/genericFormStore';
import { userDataAtom } from '../../../Atoms';
import {
  resolveFilterFields,
  collectFilterValues,
} from '../../../Components/engine/filters/resolveFilters';
import {
  LONGCHANNEL_FILTERS_GROUP,
  LONGCHANNEL_FILTERS_SURFACE,
} from '../Utils/longChannelFiltersSchema';

/**
 * The runtime values this panel publishes to the fields it renders. A field names what it wants in
 * its `contextParameters`, or a scale bound in its `minParameter` / `maxParameter`.
 */
export const LONGCHANNEL_FILTER_PARAMETERS = [
  { name: 'BranchId', label: 'BRANCH' },
  { name: 'SectionName', label: 'SECTION' },
  { name: 'Section', label: 'SECTION' },
  { name: 'UserId', label: 'USER' },
  { name: 'Locale', label: 'LANGUAGE' },
];

/**
 * Filters menu child for the long channel.
 *
 * Every field is rendered from the section's filter layout through the schema engine. The city
 * field's lookup needs the branch, which the layout cannot carry, so it is supplied here and named
 * by the field's `contextParameters`.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.layout - The filters to show, saved for the section or checked in.
 * @param {Object} props.defaultValues - Currently applied filters, used to seed the panel.
 * @param {Function} props.onConfirm - Callback. Receives the filters to apply.
 * @param {Function} props.onClose - Function. Executed on Filters menu closure.
 * @param {String} props.branchId - Selected branch, passed to lookups that ask for it.
 * @param {String} props.sectionName - The section, passed to lookups that ask for it.
 *
 * @returns {React.JSX.Element}
 */

const LongChannelFilters = function LongChannelFilters({
  layout = [],
  defaultValues = null,
  onConfirm = () => {},
  onClose = () => {},
  branchId,
  sectionName,
}) {
  const intl = useIntl();
  const payload = useAtomValue(genericFormAtom);
  const userData = useAtomValue(userDataAtom);

  const groups = useMemo(
    () => [
      {
        id: LONGCHANNEL_FILTERS_GROUP,
        items: [
          {
            id: LONGCHANNEL_FILTERS_SURFACE,
            fields: resolveFilterFields(layout, { values: defaultValues }),
          },
        ],
      },
    ],
    [layout, defaultValues]
  );

  // `SectionName` is what this panel's lookup keys have always been asked with; `Section` is the name
  // every surface publishes it by, so a condition written here reads the same elsewhere.
  const parameters = useMemo(
    () => ({
      BranchId: branchId,
      SectionName: sectionName,
      Section: sectionName,
      UserId: userData?.id,
      Locale: intl.locale,
    }),
    [branchId, sectionName, userData, intl.locale]
  );

  const handleConfirm = () => {
    onConfirm(
      collectFilterValues(
        layout,
        payload?.[LONGCHANNEL_FILTERS_GROUP]?.[LONGCHANNEL_FILTERS_SURFACE],
        parameters
      )
    );
    onClose();
  };

  return (
    <ColumnFlexBox width="min(600px, 92vw)">
      <SchemaParametersProvider parameters={parameters}>
        <SchemaWrapper
          groups={groups}
          tabValue={LONGCHANNEL_FILTERS_GROUP}
          {...SINGLE_SCHEMA}
        />
      </SchemaParametersProvider>
      <CenteredFlexBox justifyContent="flex-end" position="sticky" bottom={0}>
        <Button id="confirm-filters-btn" size="large" onClick={handleConfirm}>
          {intl.messages.CONFIRM}
        </Button>
      </CenteredFlexBox>
    </ColumnFlexBox>
  );
};

export default LongChannelFilters;
